import type { NavigateFunction } from "react-router-dom";
import { getLenis } from "../hooks/useLenis";

/**
 * Smoothly scrolls to an in-page section (`#faq`, `#contact`, …), working
 * around two real bugs that made these nav links inert:
 *
 * 1. Lenis (see hooks/useLenis.ts) hijacks wheel/touch scrolling. Plain
 *    `<a href="#faq">` native anchor jumps race with Lenis's own scroll
 *    loop and often silently no-op. We must drive the scroll through
 *    Lenis's own `scrollTo()` instead of relying on native anchor nav.
 * 2. `#faq` / `#contact` only exist in the DOM on `/` (Home). From any
 *    other route (`/admin`, `/angebote`, `/info`, …) a same-page anchor
 *    does nothing because the target element isn't there. This helper
 *    navigates to `/` first, waits a tick for Home to mount, then scrolls.
 */
export function scrollToSection(sectionId: string, navigate: NavigateFunction, currentPath: string) {
  const go = () => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const lenis = getLenis();
    if (lenis) {
      lenis.scrollTo(el, { offset: -88 }); // account for fixed 72px nav + breathing room
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (currentPath !== "/") {
    navigate(`/#${sectionId}`);
    // Home mounts on the next tick(s); poll briefly until the target
    // section exists, then scroll. Bounded so it can never loop forever.
    let attempts = 0;
    const tryScroll = () => {
      attempts += 1;
      if (document.getElementById(sectionId)) {
        go();
      } else if (attempts < 20) {
        requestAnimationFrame(tryScroll);
      }
    };
    requestAnimationFrame(tryScroll);
  } else {
    go();
  }
}

/**
 * Same scroll-after-navigate pattern as `scrollToSection`, but for anchors
 * that live on a route *other than* Home (e.g. a specific offer card on
 * `/angebote` — see ChartModal.tsx's "Premium-Report freischalten" button,
 * which needs to land on the VIP Cosmic Coaching offer specifically, not
 * just the generic /angebote page top). `targetPath` is the route that
 * owns `sectionId` in its DOM (e.g. "/angebote"); if we're already on that
 * route, this scrolls immediately without a re-navigation round-trip.
 */
export function scrollToSectionOnPath(
  sectionId: string,
  targetPath: string,
  navigate: NavigateFunction,
  currentPath: string
) {
  const go = () => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const lenis = getLenis();
    if (lenis) {
      lenis.scrollTo(el, { offset: -88 });
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (currentPath !== targetPath) {
    navigate(`${targetPath}#${sectionId}`);
    let attempts = 0;
    const tryScroll = () => {
      attempts += 1;
      if (document.getElementById(sectionId)) {
        go();
      } else if (attempts < 40) {
        // Lazy-loaded route chunk (see App.tsx's React.lazy() routes) may
        // take a few extra frames to fetch/mount compared to Home's
        // always-eager bundle — double the attempt budget vs. the Home
        // variant above.
        requestAnimationFrame(tryScroll);
      }
    };
    requestAnimationFrame(tryScroll);
  } else {
    go();
  }
}
